import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { stamp } from './ticketView';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * THE APPROVER'S DECISION, on the ticket itself (Gordon's decision 3).
 *
 * Exactly the Approvals page's decision and rules, against the same
 * endpoints, so the two cannot disagree about what approving means:
 *
 *   Approve     PUT /approvals/:id/accept { contractor_id, allocated_hours }
 *               - a contractor is required, as it is on the Approvals page;
 *               the hour budget is optional and blank means no budget (the
 *               server refuses zero for exactly that reason).
 *   Reject      PUT /approvals/:id/reject { reason } - a reason is required,
 *               and the requester is shown it.
 *   Send back   PUT /approvals/:id/send-back { question } (migration 087) -
 *               "Sent back for more information": the question is required
 *               and goes to the requester word for word (the conversation,
 *               their bell, an email); the approval waits on THEM, out of
 *               the queue, with the SLA clock stopped, until they reply and
 *               resubmit. Before 087 (or against an older server) it posts
 *               the question as a message and the ticket stays waiting, as
 *               it always did.
 *
 * Sent back, the card says so - who was asked, what, since when - and the
 * approver can still approve or reject (a phone call settled it). Back
 * again, it shows the requester's answer above the decision.
 */
export default function ApprovalCard({ ctx }) {
  const { ticket, me, reload, setNotice } = ctx;
  const [contractors, setContractors] = useState([]);
  const [who, setWho] = useState('');
  const [budget, setBudget] = useState('');
  const [mode, setMode] = useState(null);        // null | 'reject' | 'back'
  const [words, setWords] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/contractors`)
      .then((r) => {
        if (!live) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.contractors || []);
        setContractors(list.filter((c) => c.availability_status !== 'inactive'));
      })
      .catch(() => { if (live) setContractors([]); });
    return () => { live = false; };
  }, []);

  const run = async (fn, done) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      setNotice(r?.data?.warning || done);
      setMode(null);
      setWords('');
      await reload();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };

  const approve = () => {
    if (!who) { setError('Choose who will do the work.'); return; }
    run(() => axios.put(`${API_BASE}/approvals/${ticket.id}/accept`, {
      contractor_id: who,
      allocated_hours: budget.trim() === '' ? null : budget.trim()
    }), budget.trim() ? `Approved, with a budget of ${budget.trim()}h.` : 'Approved and assigned.');
  };
  const reject = () => run(() => axios.put(`${API_BASE}/approvals/${ticket.id}/reject`, { reason: words.trim() }),
    'Rejected. The requester has been shown why.');
  const first = (me?.name || '').split(/\s+/)[0];
  const requester = ticket.requester_name || 'the requester';
  const reqFirst = requester.split(/\s+/)[0];
  const sentBack = String(ticket.approval_status || '').toLowerCase() === 'sent_back';
  const round = ticket.sendback || null;
  const answeredRound = !sentBack && round && round.answered_at ? round : null;

  const sendBack = () => run(async () => {
    try {
      const r = await axios.put(`${API_BASE}/approvals/${ticket.id}/send-back`, { question: words.trim() });
      return { data: { warning: r.data?.legacy ? 'Asked. It stays waiting for your decision.' : null } };
    } catch (err) {
      // An older server has no send-back: ask the way it always was.
      if (err.response?.status !== 404) throw err;
      await axios.post(`${API_BASE}/tickets/${ticket.id}/comments`, { comment: `Before I can approve this: ${words.trim()}` });
      return { data: { warning: 'Asked. It stays waiting for your decision.' } };
    }
  }, `Sent back. ${reqFirst} has been asked, and it waits for their answer.`);

  return (
    <section className="tk-card tk-decide" id="tk-decide" tabIndex={-1} aria-labelledby="tk-decide-h">
      <h2 className="tk-decide-title" id="tk-decide-h">
        {sentBack ? `Sent back to ${reqFirst} for more information`
          : first ? `${first}, this needs your decision` : 'This needs your decision'}
      </h2>
      <p className="tk-decide-line">
        Raised {stamp(ticket.created_at)} by {requester}{ticket.department_name ? ` (${ticket.department_name})` : ''}.
      </p>
      {sentBack && round && (
        <div className="tk-decide-round" data-round="open">
          <p className="tk-decide-round-head">
            <span className="tk-chip" data-tone="amber">Waiting for {reqFirst}</span>
            <span className="tk-quiet tk-small">
              {round.asked_by_name || 'An approver'} asked {stamp(round.asked_at)} · the SLA clock is paused
            </span>
          </p>
          <blockquote className="tk-decide-q">{round.question}</blockquote>
          <p className="tk-quiet tk-small">It comes back to you when {reqFirst} answers. You can still decide now if you have what you need.</p>
        </div>
      )}
      {answeredRound && (
        <div className="tk-decide-round" data-round="answered">
          <p className="tk-decide-round-head">
            <span className="tk-chip" data-tone="green">{answeredRound.answered_by_name || reqFirst} answered</span>
            <span className="tk-quiet tk-small">{stamp(answeredRound.answered_at)}</span>
          </p>
          <p className="tk-decide-asked"><span className="tk-quiet">You asked:</span> {answeredRound.question}</p>
          <blockquote className="tk-decide-q">{answeredRound.answer}</blockquote>
        </div>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}

      {mode === null && (
        <div className="tk-decide-row">
          <label className="tk-decide-field">
            <span className="tk-label">Give it to</span>
            <select className="tk-input tk-select" value={who} onChange={(e) => setWho(e.target.value)} disabled={busy}
                    data-field="decide-contractor">
              <option value="">Choose…</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.invoices === false ? ' (in-house)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="tk-decide-field tk-decide-budget">
            <span className="tk-label">Hour budget (optional)</span>
            <input className="tk-input" type="text" inputMode="decimal" placeholder="e.g. 6" value={budget}
                   onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g, ''))} disabled={busy}
                   data-field="decide-budget" />
          </label>
          <div className="tk-decide-buttons">
            <button type="button" className="tk-btn tk-btn-go" data-action="approve" disabled={busy || !who} onClick={approve}>
              {busy ? 'Working…' : 'Approve'}
            </button>
            {!sentBack && (
              <button type="button" className="tk-btn" data-action="send-back" disabled={busy} onClick={() => { setMode('back'); setError(null); }}>
                Send back…
              </button>
            )}
            <button type="button" className="tk-btn tk-btn-danger-quiet" data-action="reject" disabled={busy} onClick={() => { setMode('reject'); setError(null); }}>
              Reject…
            </button>
          </div>
        </div>
      )}
      {mode && (
        <form className="tk-form" onSubmit={(e) => { e.preventDefault(); if (words.trim()) (mode === 'reject' ? reject() : sendBack()); }}>
          <label className="tk-label" htmlFor="tk-decide-words">
            {mode === 'reject' ? 'Why is it being rejected?' : `What do you need from ${reqFirst}?`}
          </label>
          <textarea id="tk-decide-words" className="tk-input" rows={3} value={words} autoFocus
                    onChange={(e) => setWords(e.target.value)}
                    placeholder={mode === 'reject' ? 'They are shown this, so say enough for them to know what to do next.'
                      : 'e.g. Which office is the printer in, and is it the colour one?'} />
          <p className="tk-quiet tk-small">
            {mode === 'reject' ? 'The ticket is closed and the reason is shown to the person who raised it.'
              : `${reqFirst} is shown this word for word and asked to reply. It leaves your queue until they do, and the SLA clock stops meanwhile.`}
          </p>
          <div className="tk-row">
            <button type="submit" className={`tk-btn ${mode === 'reject' ? 'tk-btn-danger' : 'tk-btn-primary'}`}
                    disabled={busy || !words.trim()}>
              {mode === 'reject' ? 'Reject' : 'Send it back'}
            </button>
            <button type="button" className="tk-btn tk-btn-quiet" onClick={() => { setMode(null); setWords(''); }}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
